import { gsap } from 'gsap'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clamp from 'lib/math/clamp'
import { cleanRect } from 'lib/math/rect'
import debounce from 'lib/util/debounce'
import { getNearestScrollingContainer } from 'lib/util/scroll'
import { useTooltipContext } from '../provider/TooltipProvider'
import computeTooltipPosition from './util/computeTooltipPosition'

/**
 * The `usePositionTooltip` hook
 *
 * @param {object} ref - the ref object
 * @param {number} preferredLocation - the preffered location angle
 * @param {number} distance - the distance to be away from the pointer
 * @returns {object} the context
 */
function usePositionTooltip (ref, preferredLocation, distance) {
  const {
    tooltipLocationStart: locationStart,
    tooltipRef,
    tooltipTarget
  } = useTooltipContext()
  const [ordinalDirection, setOrdinalDirection] = useState('n')
  const [pinPosition, setPinPosition] = useState({ x: 0, y: 0 })
  const position = useRef(locationStart)
  const resizeObserver = useRef(null)
  const rectContainer = useRef({ width: 0, height: 0 })
  const rect = useRef({ width: 0, height: 0 })
  const isMounted = useRef(false)
  const debouncedOnResize = useRef()

  /**
   * Updating the pointer size
   */
  const onPointerMove = useCallback(e => {
    position.current.x = e.clientX
    position.current.y = e.clientY

    updatePosition()
  }, [])

  /**
   * Update the position of the tooltip
   */
  const updatePosition = useCallback(() => {
    if (!isMounted.current) {
      // Tooltip already destroyed
      return
    }

    if (tooltipTarget) {
      // Is tracking an element target
      const rect = tooltipTarget.getBoundingClientRect()

      position.current.x = rect.x + rect.width * 0.5
      position.current.y = rect.y + rect.height * 0.5
    }

    const { ordinal, x, y } = computeTooltipPosition({
      target: {
        x: position.current.x - rectContainer.current.x,
        y: position.current.y - rectContainer.current.y
      },
      rect: rect.current,
      rectContainer: rectContainer.current,
      angle: preferredLocation,
      distance
    })

    gsap.set(ref.inner.current, {
      x,
      y
    })

    gsap.set(ref.outer.current, {
      x,
      y
    })

    let pinX = 0
    let pinY = 0

    pinX = clamp(
      position.current.x - x - rectContainer.current.x,
      0,
      rect.current.width
    )

    pinY = clamp(
      position.current.y - y - rectContainer.current.y,
      0,
      rect.current.height
    )

    if (ordinal === 'e' || ordinal === 'w') {
      pinY = clamp(pinY, 10, rect.current.height - 10)
    } else {
      pinX = clamp(pinX, 11, rect.current.width - 11)
    }

    setPinPosition({ x: pinX, y: pinY })
    setOrdinalDirection(ordinal)
  }, [preferredLocation, distance])

  const onResize = useCallback(() => {
    if (!isMounted.current || !ref.node.current) {
      return
    }

    rectContainer.current = cleanRect(
      tooltipRef.current.getBoundingClientRect()
    )

    rectContainer.current.height -= rectContainer.current.y
    rectContainer.current.width -= rectContainer.current.x

    rect.current = cleanRect(ref.node.current.getBoundingClientRect())

    updatePosition()
  }, [])

  /**
   * Start the tooltip positioning
   */
  const startTooltip = useCallback(() => {
    if (!tooltipTarget) {
      window.addEventListener('pointermove', onPointerMove)
    } else {
      const scrollingTarget = getNearestScrollingContainer(tooltipTarget)

      scrollingTarget.addEventListener('scroll', onResize)
    }

    debouncedOnResize?.current?.cancel()
    debouncedOnResize.current = debounce(onResize, 0)
    resizeObserver.current = new ResizeObserver(debouncedOnResize.current)

    resizeObserver.current.observe(ref.inner.current)
    resizeObserver.current.observe(tooltipRef.current)

    onResize()
  }, [])

  /**
   * Stop the tooltip positioning
   */
  const stopTooltip = useCallback(() => {
    window.removeEventListener('pointermove', onPointerMove)

    const scrollingTarget = tooltipTarget
      ? getNearestScrollingContainer(tooltipTarget)
      : window

    scrollingTarget.removeEventListener('scroll', onResize)
    debouncedOnResize?.current?.cancel()
    resizeObserver.current.disconnect()
  }, [])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    isMounted.current = true
    startTooltip()

    return () => {
      isMounted.current = false

      stopTooltip()
    }
  }, [])

  const ctx = useMemo(
    () => ({
      ordinalDirection,
      pinPosition
    }),
    [ordinalDirection, pinPosition]
  )

  return ctx
}

export default usePositionTooltip
